import { z } from 'zod';
import type { HistoryActor } from './audit';
import { idSchema, type StatusTone } from './common';
import type { ChatState, ChatToolEvent, CopilotContextRef } from './copilot';

/**
 * Historia promptów (Fala 10, DECISIONS 2026-10-07 [S] „Wszystko z codziennej pracy da się zlecić agentowi przez MCP”
 * — „historia promptów: zapisane rozmowy z Copilotem (prywatne dla osoby) i strona Historia AI z działaniami AI
 * i poleceniami, które je wywołały”).
 *
 * 1. Rozmowy z Copilotem (`ai_conversations`, `ai_messages`): zapisywane per osoba przy każdym `copilot.chat`; RLS
 *    i usługa — każdy widzi WYŁĄCZNIE swoje rozmowy (właściciel ani admin nie czyta cudzych). Lista w panelu
 *    „Zapytaj AI” (tytuł z pierwszego polecenia, data), wznowienie, wyszukiwanie, archiwizacja własnej rozmowy; przy
 *    każdej odpowiedzi — wykonane działania z linkami (zdarzenia narzędzi). Bez usuwania (retencja: decyzja 7.10).
 * 2. Historia AI (`ai_actions` + przebiegi „Zleć Claude”): działania AI w workspace z poleceniem, które je wywołało
 *    — źródło (Copilot / Claude przez MCP / automatyzacja / Zleć Claude), kto, kiedy, co zrobiono, link do rekordu,
 *    koszt (z „Widzi stawki i kwoty”); dla MCP wywołane narzędzie i pole `note` przekazane przez agenta. Osoba widzi
 *    działania na rekordach, które sama widzi (moduł + zakres rekordu), swoje działania zawsze.
 *
 * Usługi: `history.conversations.*` i `history.aiActions` (moduł `packages/core/src/modules/history`).
 */

// ── Rozmowy z Copilotem ─────────────────────────────────────────────────────

export const AI_CONVERSATION_TITLE_MAX = 120;

/** Tytuł rozmowy z pierwszego polecenia: jedna linia, do 120 znaków (z wielokropkiem). */
export function aiConversationTitle(firstMessage: string): string {
  const flat = firstMessage.replace(/\s+/g, ' ').trim();
  if (!flat) return 'Rozmowa z AI';
  return flat.length > AI_CONVERSATION_TITLE_MAX ? `${flat.slice(0, AI_CONVERSATION_TITLE_MAX - 1).trimEnd()}…` : flat;
}

export const listAiConversationsInput = z
  .object({
    /** szukaj w tytule i treści wiadomości */
    q: z.string().trim().max(100).optional(),
    includeArchived: z.boolean().optional(),
    limit: z.number().int().min(1).max(50).default(20),
    cursor: z.string().max(200).optional(),
    direction: z.enum(['forward', 'backward']).optional(),
  })
  .strict()
  .default({});
export type ListAiConversationsInput = z.input<typeof listAiConversationsInput>;

export const aiConversationIdInput = z.object({ id: idSchema }).strict();

export const archiveAiConversationInput = z.object({ id: idSchema, archived: z.boolean().default(true) }).strict();
export type ArchiveAiConversationInput = z.input<typeof archiveAiConversationInput>;

export interface AiConversationListItem {
  id: string;
  title: string;
  createdAt: Date;
  lastMessageAt: Date;
  messageCount: number;
  archivedAt: Date | null;
  /** kontekst widoku, z którego zaczęto rozmowę (np. klient) */
  contextRef: CopilotContextRef | null;
  contextLabel: string | null;
}

export interface AiConversationList {
  items: AiConversationListItem[];
  nextCursor: string | null;
}

export interface AiConversationMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: Date;
  /** odpowiedź: wykonane działania (zdarzenia narzędzi z linkami) */
  toolEvents: ChatToolEvent[];
  state: ChatState | null;
  aiJobId: string | null;
  /** podpis odpowiedzi (dostawca, model, symulacja) */
  meta: { provider: string; model: string | null; simulated: boolean } | null;
}

export interface AiConversationDetail extends AiConversationListItem {
  messages: AiConversationMessage[];
}

export const AI_CONVERSATION_MESSAGES = {
  notFound: 'Nie znaleziono rozmowy',
  archived: 'Rozmowa jest w archiwum — przywróć ją, żeby kontynuować',
} as const;

// ── Historia AI (działania AI w workspace) ──────────────────────────────────

export const AI_ACTION_SOURCES = ['copilot', 'mcp', 'automation', 'delegate'] as const;
export type AiActionSource = (typeof AI_ACTION_SOURCES)[number];

/** Stan pojedynczego działania (jak zdarzenie narzędzia Copilota). */
export const AI_ACTION_STATUSES = ['ok', 'error', 'waiting_approval', 'needs_confirmation', 'needs_choice'] as const;
export type AiActionStatus = (typeof AI_ACTION_STATUSES)[number];

/** Działanie w zapisie (`ai_actions.actions`): rekord wyniku z klientem / projektem do kontroli widoczności. */
export interface AiActionRecord {
  /** narzędzie (Copilot / MCP) albo akcja audytu */
  tool: string;
  status: AiActionStatus;
  /** zdanie po polsku („Dodano zadanie #12 Stopka”) */
  label: string;
  link: { type: string; id: string } | null;
  clientId: string | null;
  projectId: string | null;
}

/** Wywołane narzędzie (MCP) z polem `note` od agenta. */
export interface AiActionToolCall {
  name: string;
  /** pole `note` przekazane przez agenta (bez sekretów, do 2000 znaków) */
  note: string | null;
  ok: boolean;
  /** kod błędu (VALIDATION_ERROR, FORBIDDEN, …) */
  code: string | null;
}

export const listAiActionsInput = z
  .object({
    source: z.enum(AI_ACTION_SOURCES).optional(),
    userId: idSchema.optional(),
    clientId: idSchema.optional(),
    /** także odczyty MCP (wyszukiwanie, kontekst) — domyślnie tylko działania ze zmianą */
    includeReads: z.boolean().optional(),
    limit: z.number().int().min(1).max(100).default(30),
    cursor: z.string().max(300).optional(),
    direction: z.enum(['forward', 'backward']).optional(),
  })
  .strict()
  .default({});
export type ListAiActionsInput = z.input<typeof listAiActionsInput>;

export interface AiHistoryAction {
  tool: string;
  status: AiActionStatus;
  label: string;
  link: { type: string; id: string; clientId: string | null } | null;
}

export interface AiHistoryEntry {
  /** `<źródło>:<id>` */
  key: string;
  source: AiActionSource;
  at: Date;
  /** osoba, w imieniu której działało AI (null = automatyzacja bez osoby) */
  person: HistoryActor | null;
  /** „Copilot”, „Claude przez MCP · token Laptop”, „Automatyzacja”, „Zleć Claude · sesja Claude (MCP)” */
  sourceLabel: string;
  /** polecenie, które wywołało działanie (null = brak albo ukryte) */
  command: string | null;
  /** polecenie ukryte: prywatna poczta innej osoby (automatyzacja z wyzwalaczem albo podsumowaniem z jej skrzynki osobistej) */
  commandHidden: boolean;
  /** MCP: wywołane narzędzia z polem `note` od agenta */
  tools: AiActionToolCall[];
  /** co zrobiono (tylko działania na rekordach widocznych dla osoby) */
  actions: AiHistoryAction[];
  /** działania poza zakresem osoby (pominięte w `actions`) */
  hiddenActions: number;
  client: { id: string; name: string } | null;
  /** koszt w PLN (tylko z „Widzi stawki i kwoty”; przebieg Claude z subskrypcji = informacyjnie) */
  costPln?: string;
  costInformational?: boolean;
  simulated: boolean;
  status: { label: string; tone: StatusTone } | null;
  /** rozmowa (tylko własna — link do wznowienia w panelu) */
  conversationId: string | null;
  aiJobId: string | null;
  agentRunId: string | null;
}

export interface AiHistoryPage {
  items: AiHistoryEntry[];
  nextCursor: string | null;
}
